import { type LoadableEntry } from "~/app/store/loadableTypes";
import { useCallback, useEffect, useRef, useState } from "react";
import throttle from "lodash/throttle";
import debounce from "lodash/debounce";
import { getNextUID } from "~/common/utils/uid";

interface Options<DATA> {
  mode: "throttle" | "debounce";
  time: number;
  onLoad?: (data: DATA) => void;
}

export function useFetch<DATA, QUERY>(
  fetchFunc: (query: QUERY) => Promise<DATA>,
  query: QUERY,
  { mode, time, onLoad }: Options<DATA> = {
    mode: "throttle",
    time: 1000,
  },
): LoadableEntry<DATA> {
  const [data, setData] = useState<LoadableEntry<DATA>>({ state: "loading" });

  const guidRef = useRef(-1);

  const wrapperFunc = mode === "throttle" ? throttle : debounce;

  const fetchDataCb = useCallback(
    wrapperFunc((query: QUERY) => {
      const guid = getNextUID();
      guidRef.current = guid;

      setData((prev) => {
        if (prev.state !== "loading") {
          return { ...prev, state: "loading" };
        }
        return prev;
      });

      fetchFunc(query)
        .then((res: DATA) => {
          onLoad?.(res);
          if (guidRef.current === guid) {
            setData({ state: "loaded", data: res });
          }
        })
        .catch(() => {
          if (guidRef.current === guid) {
            setData({ state: "error" });
          }
        });
    }, time),
    [],
  );

  useEffect(() => {
    fetchDataCb(query);
  }, [query]);

  return data;
}
